import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import GlobalSearch from 'shared/components/inputs/GlobalSearch';
import {
  buttonClasses,
  chipClasses,
  paginationClasses,
  buttonRowClasses,
} from 'styles/tailwindClassnames';
import SelectInput from 'shared/components/inputs/SelectInput';
import Pagination from 'shared/components/ui/Pagination';
import { page_size } from 'shared/api/apiUtils';
import { getConnectionAccount } from 'connections/ConnectionHelperFunctions';
import { Icon } from 'react-icons-kit';
import { ic_close } from 'react-icons-kit/md/ic_close';

type Candidate = {
  id: string;
  name: string;
  base_url?: string;
  type?: { id: string; name: string };
  configurations?: any[];
};
type FilterOption = { label: string; value: string };

type AddDestinationDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  candidates: Candidate[];
  loading: boolean;
  onAttach: (ids: string[]) => Promise<void> | void;
  onCreateNew: () => void;
  onSearch: (searchStr: string) => void;
  // The same numbered pagination the Routes list draws: the API's total
  // for the current query, the offset of the page on screen, and a ReactPaginate
  // page-click handler ({ selected }) that replaces the rows with that page.
  totalCount: number;
  pageOffset: number;
  onPageChange: (e: { selected: number }) => void;
  // Workspace filter — defaults to the Route's workspace, clearable to all.
  workspaceOptions: FilterOption[];
  workspaceFilter: FilterOption | null;
  onChangeWorkspaceFilter: (option: FilterOption | null) => void;
  loadingWorkspaces?: boolean;
};

// Same shell conventions as TransformationRulesPanel: fixed right-hand panel that
// slides in, header + scrollable body + sticky footer.
const AddDestinationDrawer = ({
  isOpen,
  onClose,
  candidates,
  loading,
  onAttach,
  onCreateNew,
  onSearch,
  totalCount,
  pageOffset,
  onPageChange,
  workspaceOptions,
  workspaceFilter,
  onChangeWorkspaceFilter,
  loadingWorkspaces,
}: AddDestinationDrawerProps) => {
  const { t } = useTranslation();
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setChecked({});
      setSaving(false);
      setQuery('');
    }
  }, [isOpen]);

  const selectedIds = Object.keys(checked).filter((id) => checked[id]);

  return (
    <div
      data-testid="AddDestinationDrawer-panel"
      className={`fixed right-0 top-0 z-[56] flex h-full w-full flex-col border-l border-ui-divider-lines bg-white shadow-xl transition-transform duration-300 md:z-[60] md:w-[420px] ${
        isOpen ? 'translate-x-0' : 'pointer-events-none translate-x-full'
      }`}
      aria-hidden={!isOpen}
      // The panel stays mounted so it can slide, so it also has to be taken
      // out of the tab order while closed — aria-hidden with focusable
      // children is an a11y violation. `inert` is not typed on React 18's
      // HTMLAttributes yet, hence the cast.
      {...({ inert: isOpen ? undefined : '' } as any)}
    >
      <div className="flex items-center justify-between border-b border-ui-divider-lines px-4 py-4">
        <h2 className="text-body-lg font-bold text-ui-text-primary-text">
          {t('Add destination')}
        </h2>
        <button
          onClick={onClose}
          aria-label={t('Close')}
          className="flex h-9 w-9 items-center justify-center text-ui-text-secondary-text hover:text-ui-text-primary-text"
        >
          <Icon icon={ic_close} size={24} />
        </button>
      </div>

      <div className="flex flex-col gap-3 px-4 pt-4">
        {/* The shared search box, same as every list page: the term is
                    applied on Enter or the search button, not as you type. */}
        <GlobalSearch
          onChangeGlobalSearch={(value) => {
            setQuery(value);
            onSearch(value);
          }}
          savedString={query}
          placeholder={t('Search destinations')}
        />
        <div className="flex flex-col gap-1">
          {/* Not filterClasses.label: that is the on-tint treatment, and this
              drawer is white. */}
          <span className="text-body-sm font-normal text-ui-text-primary-text">
            {t('Workspace')}
          </span>
          <SelectInput
            name="drawer_workspace"
            options={workspaceOptions}
            value={workspaceFilter}
            onChange={onChangeWorkspaceFilter}
            isMulti={false}
            isClearable={true}
            isSearchable={true}
            isDisabled={false}
            isLoading={!!loadingWorkspaces}
            className=""
            variant="routeFilter"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        {loading ? (
          [0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="mb-2 h-10 w-full animate-pulse rounded bg-teal-t30"
            />
          ))
        ) : candidates.length === 0 ? (
          <p className="mb-4 text-body-sm text-ui-text-primary-text">
            {query
              ? t('No destinations match your search.')
              : workspaceFilter
                ? t('No other destinations in this workspace yet.')
                : t('No other destinations available.')}
          </p>
        ) : (
          candidates.map((c) => (
            <label
              key={c.id}
              data-testid="AddDestinationDrawer-candidate-row"
              className="flex cursor-pointer items-start gap-3 border-b border-ui-divider-lines py-2"
            >
              <input
                type="checkbox"
                data-testid="AddDestinationDrawer-candidate-checkbox"
                className="mt-1 accent-teal-t100"
                checked={!!checked[c.id]}
                onChange={(e) =>
                  setChecked((prev) => ({ ...prev, [c.id]: e.target.checked }))
                }
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-body-sm text-ui-text-primary-text">
                  {c.name}
                </span>
                {c.base_url && (
                  <span className="block truncate text-body-sm text-ui-text-primary-text">
                    {c.base_url}
                  </span>
                )}
                {/* EarthRanger only: which ER account this destination logs in with */}
                {(() => {
                  const account = getConnectionAccount(c as any);
                  return account ? (
                    <span className="block truncate text-body-sm text-ui-text-primary-text">
                      {t('Account')}:{' '}
                      <span className="font-mono">{account}</span>
                    </span>
                  ) : null;
                })()}
              </span>
              {c.type?.name && (
                <span className={`flex-none ${chipClasses.tag}`}>
                  {c.type.name}
                </span>
              )}
            </label>
          ))
        )}
        {totalCount > 0 && (
          <Pagination
            showText={true}
            handlePageClick={onPageChange}
            pageCount={Math.ceil(totalCount / page_size)}
            resultStart={pageOffset + 1}
            resultEnd={
              pageOffset + page_size < totalCount
                ? pageOffset + page_size
                : totalCount
            }
            totalCount={totalCount}
            initialPage={Math.floor(pageOffset / page_size)}
            take={page_size}
            loading={loading}
            // The drawer is much narrower than a list page, so fewer
            // page numbers fit before the row wraps.
            pageRangeDisplayed={2}
            marginPagesDisplayed={1}
            mainclassname={paginationClasses.container}
          />
        )}

        {/* Secondary path, deliberately not button-shaped: a full-width
                    button here sat closer to the rows than the footer's Add to
                    Route and got clicked when the intent was to select an
                    existing destination (GUNDI-5638). */}
        <p className="mt-4 text-body-sm text-ui-text-primary-text">
          {t("Can't find the destination you need?")}{' '}
          <button
            type="button"
            onClick={onCreateNew}
            data-testid="AddDestinationDrawer-create-new-button"
            className={buttonClasses.inlineLink}
          >
            {t('Create new destination')}
          </button>
        </p>
      </div>

      <div className="flex items-center gap-3 border-t border-ui-divider-lines px-4 py-3">
        {/* Checked rows survive a page change, so say how many are selected —
                    otherwise a selection made two pages back is invisible and the
                    user attaches more than the empty checkboxes on screen suggest. */}
        {selectedIds.length > 0 && (
          <span className="order-last ml-auto text-body-sm text-ui-text-primary-text">
            {t('{{count}} selected', { count: selectedIds.length })}
          </span>
        )}
        <div className={buttonRowClasses.row}>
          <button
            data-testid="AddDestinationDrawer-attach-button"
            className={buttonClasses.primaryAction}
            disabled={selectedIds.length === 0 || saving}
            onClick={async () => {
              setSaving(true);
              try {
                await onAttach(selectedIds);
              } finally {
                setSaving(false);
              }
            }}
          >
            {t('Add to Route')}
          </button>
          <button className={buttonClasses.cancelAction} onClick={onClose}>
            {t('Cancel')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddDestinationDrawer;
